import { useContext, useState } from "react"
import { JSONSchema7 } from "json-schema"

import {
    DataFieldType,
    DocumentType,
    FieldStatus
} from "../../../../../../Models/DataProgram/DocumentExtraction"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { DocumentsExtractionContext } from "../../DocumentsExtractionContext"
import { FieldModalType } from "../Modals/FieldModal"

export type DocumentsListItemProps = {
    schema: JSONSchema7
    value: DocumentType
    onDelete: () => void
    onEdit: (value: DocumentType) => void
}

export type FieldModalOptions = {
    index?: number
    type?: FieldModalType
}

export const useDocumentsListItem = ({
    value,
    onDelete,
    onEdit
}: DocumentsListItemProps) => {
    const { annotationFields = [] } = value
    const { annotationsOutputList, setAnnotationsOutputList } = useContext(
        DocumentsExtractionContext
    )
    const editDocumentState = useBoolean(false)
    const editFieldState = useBoolean(false)
    const deleteModalState = useBoolean(false)
    const fieldModalOptionsState = useState<FieldModalOptions>({})
    const deleteHandlerState = useState<() => void>(() => {})

    const [, , closeEditDocumentModal] = editDocumentState
    const [, openFieldModal] = editFieldState
    const [, openDeleteModal] = deleteModalState
    const [, setFieldModalOptions] = fieldModalOptionsState
    const [, setDeleteHandler] = deleteHandlerState

    const handleDocumentSubmit = (value: DocumentType) => {
        onEdit(value)
        closeEditDocumentModal()
    }

    const handleOpenFieldModal = () => {
        setFieldModalOptions({
            index: undefined,
            type: FieldModalType.New
        })
        openFieldModal()
    }

    const handleChangeField = (
        field: DataFieldType,
        { type, index }: FieldModalOptions
    ) => {
        const fields = [...annotationFields]
        if (type === FieldModalType.Edit && index !== undefined) {
            fields.splice(index, 1, field)
        } else {
            fields.push(field)
        }
        onEdit({
            ...value,
            annotationFields: fields
        })
    }

    const handleEditField = (options: FieldModalOptions) => () => {
        setFieldModalOptions(options)
        openFieldModal()
    }

    const handleDeleteOpen = (callback: () => void) => () => {
        setDeleteHandler(() => callback)
        openDeleteModal()
    }

    const handleDeleteField = (index: number) => () => {
        const fields = [...(value.annotationFields || [])]
        if (annotationsOutputList) {
            delete annotationsOutputList?.annotations?.[
                fields[index].fieldId.data
            ]
            setAnnotationsOutputList(annotationsOutputList)
        }
        fields?.splice(index, 1)
        onEdit({
            ...value,
            annotationFields: [...fields]
        })
    }

    const handleStatusChange = (
        type: "annotationFields" | "presetFields",
        prevValue: FieldStatus,
        index: number
    ) => {
        const fields = [...(value[type] || [])]
        fields.splice(index, 1, {
            ...fields[index],
            status: {
                ...fields[index].status,
                data:
                    prevValue === FieldStatus.Active
                        ? FieldStatus.Inactive
                        : FieldStatus.Active
            }
        })
        onEdit({
            ...value,
            [type]: [...fields]
        })
    }

    const handleDrop = (items: DataFieldType[]) => {
        const fields = [...(items || [])]
        onEdit({
            ...value,
            annotationFields: fields
        })
    }

    return {
        editDocumentState,
        editFieldState,
        deleteModalState,
        fieldModalOptionsState,
        deleteHandlerState,
        openDeleteDocument: handleDeleteOpen(onDelete),
        openNewField: handleOpenFieldModal,
        openFieldEdit: handleEditField,
        openFieldDelete: handleDeleteOpen,
        onDrop: handleDrop,
        onStatusChange: handleStatusChange,
        onFieldChange: handleChangeField,
        onFieldDelete: handleDeleteField,
        onDocumentSubmit: handleDocumentSubmit
    }
}
